class ProgressiveMegaMenu extends HTMLElement {
  connectedCallback() {
    this.rootPanelId = this.dataset.rootPanel;
    this.activeTrigger = null;
    this.listItem = this.closest('.menu-list__list-item');
    this.topLevelLink = this.listItem?.querySelector(':scope > .menu-list__link');

    this.addEventListener('click', this.onClick);
    this.addEventListener('keydown', this.onKeyDown);
    this.listItem?.addEventListener('pointerleave', this.onMenuLeave);
    this.topLevelLink?.addEventListener('click', this.onTopLevelClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('keydown', this.onKeyDown);
    this.listItem?.removeEventListener('pointerleave', this.onMenuLeave);
    this.topLevelLink?.removeEventListener('click', this.onTopLevelClick);
  }

  onTopLevelClick = (event) => {
    const headerComponent = this.closest('header-component');
    if (headerComponent?.dataset.menuStyle !== 'menu') return;

    event.preventDefault();
    const headerMenu = this.closest('header-menu');
    if (headerMenu && this.listItem) {
      headerMenu.activate({ target: this.listItem });
      requestAnimationFrame(() => this.updateHeaderHeight());
    }
  };

  onClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const openButton = event.target.closest('[data-progressive-open]');
    if (openButton instanceof HTMLButtonElement) {
      const panelId = openButton.dataset.progressiveOpen;
      if (!panelId) return;

      this.openPanel(panelId, openButton);
      return;
    }

    const backButton = event.target.closest('[data-progressive-back]');
    if (backButton instanceof HTMLButtonElement) {
      this.showRoot(true);
    }
  };

  onKeyDown = (event) => {
    if (event.key !== 'Escape' && event.key !== 'ArrowLeft') return;

    const rootPanel = this.getPanel(this.rootPanelId);
    if (!rootPanel?.hidden) return;

    event.preventDefault();
    event.stopPropagation();
    this.showRoot(true);
  };

  onMenuLeave = () => {
    this.showRoot(false);
  };

  openPanel(panelId, trigger) {
    const panel = this.getPanel(panelId);
    const rootPanel = this.getPanel(this.rootPanelId);
    if (!panel || !rootPanel) return;

    this.querySelectorAll('[data-progressive-open]').forEach((button) => {
      button.setAttribute('aria-expanded', button === trigger ? 'true' : 'false');
    });

    this.querySelectorAll('[data-progressive-panel]').forEach((item) => {
      item.hidden = item !== panel;
      item.classList.toggle('is-active', item === panel);
    });

    this.activeTrigger = trigger;
    this.updateHeaderHeight();

    requestAnimationFrame(() => {
      panel.querySelector('[data-progressive-back]')?.focus({ preventScroll: true });
    });
  }

  showRoot(restoreFocus) {
    const rootPanel = this.getPanel(this.rootPanelId);
    if (!rootPanel) return;

    this.querySelectorAll('[data-progressive-panel]').forEach((panel) => {
      panel.hidden = panel !== rootPanel;
      panel.classList.toggle('is-active', panel === rootPanel);
    });

    this.querySelectorAll('[data-progressive-open]').forEach((button) => {
      button.setAttribute('aria-expanded', 'false');
    });

    const trigger = this.activeTrigger;
    this.activeTrigger = null;
    this.updateHeaderHeight();

    if (restoreFocus && trigger instanceof HTMLElement) {
      requestAnimationFrame(() => trigger.focus({ preventScroll: true }));
    }
  }

  getPanel(panelId) {
    if (!panelId) return null;
    return this.querySelector(`#${CSS.escape(panelId)}`);
  }

  updateHeaderHeight() {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const submenu = this.closest('.menu-list__submenu');
        const headerComponent = this.closest('header-component');
        if (!(submenu instanceof HTMLElement) || !(headerComponent instanceof HTMLElement)) return;

        const submenuHeight = submenu.offsetHeight;
        const overlapsBottomRow = headerComponent.hasAttribute('data-submenu-overlap-bottom-row');
        const visibleHeader = overlapsBottomRow
          ? headerComponent.querySelector('.header__row--top')?.offsetHeight || 0
          : headerComponent.offsetHeight;

        headerComponent.style.setProperty('--submenu-height', `${submenuHeight}px`);
        headerComponent.style.setProperty('--full-open-header-height', `${submenuHeight + visibleHeader}px`);
      });
    });
  }
}

if (!customElements.get('progressive-mega-menu')) {
  customElements.define('progressive-mega-menu', ProgressiveMegaMenu);
}
